import {
  constructFeaturebaseJwtUrl,
  ErrorLevel,
  isValidFeaturebaseUrl,
} from '@griply/utils';
import { jwtDecode } from 'jwt-decode';
import {
  QueryParamOptions,
  redirectWithParameters,
} from 'shared/lib/react-router-dom';
import { Paths } from 'shared/routes/paths';
import {
  getFeaturebaseToken,
  getGoals,
  getUser,
  signInWithDesktopCode,
  signInWithToken,
  signOutUser,
} from 'shared/services/backend-api';
import { getErrorReporter } from 'shared/services/error-tracking';
import { getSearchParamIdToken } from 'shared/utils/get-search-param-id-token';
import { isProductionEnvironment } from 'shared/utils/is-environment';

export const userLoader = async () => {
  if (!isProductionEnvironment) {
    // eslint-disable-next-line no-console -- non-prod logging
    console.log(
      `[Debug-Loader] userLoader started at ${performance.now().toFixed(0)}ms`,
    );
  }

  const user = await getUser();

  if (!user) {
    if (!isProductionEnvironment) {
      // eslint-disable-next-line no-console -- non-prod logging
      console.warn(
        '[Debug-Loader] ❌ getUser returned null. Triggering REDIRECT to Login.',
      );
    }
    throw redirectWithParameters(Paths.Login);
  }

  if (!isProductionEnvironment) {
    // eslint-disable-next-line no-console -- non-prod logging
    console.log('[Debug-Loader] ✅ User found, proceeding.');
  }
  return user;
};

export const loginLoader = async () => {
  // Sign-in strips the apple token from the url, and its state carries the redirect parameters.
  const appleIdToken = getSearchParamIdToken();
  await signInWithDesktopCode();
  await signInWithToken();
  const user = await getUser();

  if (user) {
    const desktopChallenge = new URLSearchParams(window.location.search).get(
      QueryParamOptions.DesktopChallenge,
    );

    // The browser half of the desktop hand-off ends here: mint the code on the confirmation page, not in a loader.
    if (desktopChallenge) {
      throw redirectWithParameters(Paths.DesktopConnected);
    }

    // Check for Featurebase SSO params
    const urlSearchParams = new URLSearchParams(window.location.search);
    const isFeaturebaseSso = urlSearchParams.get('featurebase') === 'true';
    const returnTo = urlSearchParams.get('return_to');

    // If SSO params present, attempt Featurebase redirect
    if (isFeaturebaseSso && returnTo) {
      // Validate return_to domain (security: prevent open redirect)
      if (!isValidFeaturebaseUrl(returnTo)) {
        getErrorReporter().captureMessage(
          'Invalid Featurebase SSO return_to URL',
          ErrorLevel.Warning,
          { tags: { source: 'featurebase-sso' }, extra: { returnTo } },
        );
        // Fall through to normal login redirect below
      } else {
        // Fetch JWT token and redirect to Featurebase
        try {
          const { token } = await getFeaturebaseToken();
          const featurebaseRedirectUrl = constructFeaturebaseJwtUrl(
            token,
            returnTo,
          );
          // External redirect -- cannot use React Router redirect() for external URLs
          window.location.href = featurebaseRedirectUrl;
          // Return never-resolving promise to prevent React Router from navigating
          return new Promise(() => {});
        } catch (error) {
          getErrorReporter().captureException(error, {
            tags: { source: 'featurebase-sso' },
          });
          // Fall through to normal login redirect below
        }
      }
    }

    const goals = await getGoals();
    let extraParams = {};
    // get the queryParameters from the start of login to go with
    try {
      const { state } = appleIdToken
        ? jwtDecode<{ state: string }>(appleIdToken)
        : ({} as { state: undefined });
      extraParams = state ? JSON.parse(state) : {};
    } catch (error) {
      getErrorReporter().captureException(error, {
        tags: { source: 'parse-search-params-jwt' },
      });
    }

    throw redirectWithParameters(
      goals.length ? Paths.Home : Paths.Goals,
      extraParams,
    );
  }

  return null;
};

export const completeSignupLoader = async () => {
  // make sure that if someone has login credentials it is handled
  await signInWithToken();
  const user = await getUser();

  // make sure the user is signed out before continuing
  if (user) {
    await signOutUser();
  }

  return true;
};
